/**
 * Generate sample Excel file for transaction reversal
 * This creates a properly formatted CSV file with demo data
 */

export interface ReversalSampleRow {
  'Original Txn Date': string
  'Txn Rail': '1link' | 'RAAST' | 'RTGS'
  Partner: string
  'Txn Amount': number
  'CBS Reference': string
  'Txn Reference': string
  'Receiver IBAN': string
  'Receiver Name': string
}

export const generateReversalSampleData = (): ReversalSampleRow[] => {
  const today = new Date()
  const formatDate = (daysAgo: number) => {
    const date = new Date(today)
    date.setDate(date.getDate() - daysAgo)
    return date.toISOString().split('T')[0]
  }

  return [
    {
      'Original Txn Date': formatDate(5),
      'Txn Rail': '1link',
      Partner: 'RIA Money Transfer',
      'Txn Amount': 50000,
      'CBS Reference': 'CBS2024001234',
      'Txn Reference': '123456',
      'Receiver IBAN': 'PK36BIPL0000001234567890', // Exactly 24 chars: PK + 22 digits
      'Receiver Name': 'Muhammad Ahmed Khan',
    },
    {
      'Original Txn Date': formatDate(4),
      'Txn Rail': 'RAAST',
      Partner: 'InstantCash',
      'Txn Amount': 75000,
      'CBS Reference': 'CBS2024005678',
      'Txn Reference': 'RAAST2024011600001',
      'Receiver IBAN': 'PK36BIPL0000009876543210', // Exactly 24 chars: PK + 22 digits
      'Receiver Name': 'Fatima Siddiqui',
    },
    {
      'Original Txn Date': formatDate(3),
      'Txn Rail': 'RTGS',
      Partner: 'MoneyGram',
      'Txn Amount': 100000,
      'CBS Reference': '',
      'Txn Reference': 'RTGS-2024-001-ABC',
      'Receiver IBAN': 'PK36BIPL0000001122334455', // Exactly 24 chars: PK + 22 digits
      'Receiver Name': 'Ali Hassan',
    },
    {
      'Original Txn Date': formatDate(2),
      'Txn Rail': '1link',
      Partner: 'Western Union',
      'Txn Amount': 35000,
      'CBS Reference': 'CBS2024009012',
      'Txn Reference': '789012',
      'Receiver IBAN': 'PK36BIPL0000005566778899', // Exactly 24 chars: PK + 22 digits
      'Receiver Name': 'Sara Malik',
    },
    {
      'Original Txn Date': formatDate(1),
      'Txn Rail': 'RAAST',
      Partner: 'Xpress Money',
      'Txn Amount': 62500,
      'CBS Reference': 'CBS2024003456',
      'Txn Reference': 'RAAST2024011700002',
      'Receiver IBAN': 'PK36BIPL0000002233445566', // Exactly 24 chars: PK + 22 digits
      'Receiver Name': 'Bilal Ahmad',
    },
    {
      'Original Txn Date': formatDate(1),
      'Txn Rail': '1link',
      Partner: 'RIA Money Transfer',
      'Txn Amount': 45000,
      'CBS Reference': 'CBS2024007890',
      'Txn Reference': '345678',
      'Receiver IBAN': 'PK36BIPL0000007788990011', // Exactly 24 chars: PK + 22 digits
      'Receiver Name': 'Ayesha Noor',
    },
    {
      'Original Txn Date': formatDate(0),
      'Txn Rail': 'RTGS',
      Partner: 'IME (International Money Express)',
      'Txn Amount': 125000,
      'CBS Reference': 'CBS2024004567',
      'Txn Reference': 'RTGS-2024-002-XYZ',
      'Receiver IBAN': 'PK36BIPL0000003344556677', // Exactly 24 chars: PK + 22 digits
      'Receiver Name': 'Usman Tariq',
    },
  ]
}

export const downloadReversalSampleExcel = () => {
  const data = generateReversalSampleData()

  // Verify all IBANs are exactly 24 characters
  data.forEach((row, index) => {
    if (row['Receiver IBAN'].length !== 24) {
      console.error(
        `Row ${index + 1}: IBAN length is ${row['Receiver IBAN'].length}, expected 24`,
      )
    }
  })

  // Create CSV content with proper escaping
  const headers = [
    'Original Txn Date',
    'Txn Rail',
    'Partner',
    'Txn Amount',
    'CBS Reference',
    'Txn Reference',
    'Receiver IBAN',
    'Receiver Name',
  ]

  const escapeCSV = (value: string | number) => {
    const str = String(value)
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`
    }
    return str
  }

  const csvRows = [
    headers.join(','),
    ...data.map((row) =>
      [
        row['Original Txn Date'],
        row['Txn Rail'],
        escapeCSV(row['Partner']),
        row['Txn Amount'],
        row['CBS Reference'],
        row['Txn Reference'],
        row['Receiver IBAN'],
        escapeCSV(row['Receiver Name']),
      ].join(','),
    ),
  ]

  const csvContent = csvRows.join('\n')

  // Add UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `transaction_reversal_sample_${new Date().toISOString().split('T')[0]}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
